import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FORM_APP_DEFINITIONS } from '@/features/Dashboard/apps';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import { useCurrentDashboardTabConfig } from '@/features/Dashboard/hooks/useCurrentDashboardConfig';
import { selectCurrentTiles } from '@/features/Dashboard/pages/Edit/model/dashboardEdit.selectors';
import { AppType } from '@/features/Dashboard/types';
import { convertTilesToActiveSymbols } from '@/features/Dashboard/utils/tileSymbolConverter';
import { useAppSelector } from '@/hooks/redux-hooks';
import type { SetState } from '@/types/helpers';

import { DrawerStep } from '@/features/Dashboard/drawer/types';

export { DrawerStep };

interface DrawerStepsContextValue {
  drawerStep: DrawerStep | null;

  closeDrawer: () => void;
  setDrawerStep: SetState<DrawerStep | null>;
  isEditMode: boolean;
  setIsEditMode: SetState<boolean>;
  // When editing an existing item, holds its identifiers
  editingTarget: { appId?: string; tabId?: string; tileId?: string } | null;
  setEditingTarget: (target: { appId?: string; tabId?: string; tileId?: string } | null) => void;
  preventClose: boolean;
  setPreventClose: SetState<boolean>;
  confirmOpen: boolean;
  setConfirmOpen: SetState<boolean>;
  handleAttemptClose: () => void;
  handleConfirmClose: () => void;
  handleCancelClose: () => void;
  handleAttemptBack: () => void;
  setHasChanges: SetState<boolean>;
}

const DrawerStepsContext = createContext<DrawerStepsContextValue | undefined>(undefined);

export const useDrawerSteps = () => {
  const ctx = useContext(DrawerStepsContext);
  if (!ctx) throw new Error('useDrawerSteps must be used within DrawerStepsProvider');
  return ctx;
};

export const DrawerStepsProvider = ({ children }: { children: React.ReactNode }) => {
  const [drawerStep, setDrawerStep] = useState<DrawerStep | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTarget, setEditingTarget] = useState<{
    appId?: string;
    tabId?: string;
    tileId?: string;
  } | null>(null);
  const [preventClose, setPreventClose] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const close = useRef(false);

  const [hasChanges, setHasChanges] = useState(false);
  const { resetAppForm, setInitialValues, mode } = useAppForm();
  const draftTiles = useAppSelector(selectCurrentTiles);

  const closeDrawer = useCallback(() => {
    resetAppForm();
    setDrawerStep(null);
    setPreventClose(false);
    setIsEditMode(false);
    setEditingTarget(null);
    setHasChanges(false);
  }, [resetAppForm]);

  const handleAttemptClose = useCallback(() => {
    // Prevent closing the drawer on backdrop or ESC when blocked
    if (preventClose) return;
    close.current = true;
    // If there are unsaved changes, show confirm dialog
    if (hasChanges) {
      setConfirmOpen(true);
      return;
    }

    closeDrawer();
  }, [preventClose, hasChanges, closeDrawer]);

  const handleConfirmClose = useCallback(() => {
    setConfirmOpen(false);
    if (close.current) {
      // User intended to close the drawer
      // (backdrop/Escape/close button): always close
      closeDrawer();
    } else if (isEditMode && editingTarget) {
      // User was editing existing item and clicked back - close drawer
      closeDrawer();
    } else {
      // User was adding new item and clicked back - go to app selector
      setDrawerStep(DrawerStep.SelectApp);
      setEditingTarget(null);
      resetAppForm();
    }
  }, [closeDrawer, isEditMode, editingTarget, resetAppForm]);

  const handleCancelClose = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const handleAttemptBack = useCallback(() => {
    if (preventClose) return;

    if (hasChanges) {
      close.current = false;
      setConfirmOpen(true);
      return;
    }

    if (isEditMode && editingTarget) {
      // Editing existing item - close drawer entirely
      closeDrawer();
    } else {
      // Adding new item - go back to app selector, keep isEditMode
      setDrawerStep(DrawerStep.SelectApp);
      setEditingTarget(null);
      resetAppForm();
    }
  }, [preventClose, hasChanges, isEditMode, editingTarget, resetAppForm, closeDrawer]);

  const dashboardConfig = useCurrentDashboardTabConfig();

  useEffect(() => {
    if (editingTarget && drawerStep !== DrawerStep.SelectHighlightedPrice) return;

    const initialValuesByStep = {
      [DrawerStep.SelectSpotChart]: FORM_APP_DEFINITIONS[AppType.Spot].initialValues,
      [DrawerStep.SelectFWDChart]: FORM_APP_DEFINITIONS[AppType.FWD].initialValues,
      [DrawerStep.SelectBasicDataTable]: FORM_APP_DEFINITIONS[AppType.Table].initialValues,
      [DrawerStep.SpotHistoryTable]: FORM_APP_DEFINITIONS[AppType.SpotHistoryTable].initialValues
    };

    if (drawerStep === DrawerStep.SelectHighlightedPrice) {
      let tiles: ReturnType<typeof convertTilesToActiveSymbols> = [];
      if (mode === 'create') {
        if (dashboardConfig?.[0] && 'type' in dashboardConfig[0]) {
          tiles = convertTilesToActiveSymbols(dashboardConfig[0].tiles);
        }
      } else {
        tiles = convertTilesToActiveSymbols(draftTiles);
      }
      setInitialValues({ type: AppType.Tiles, symbols: { active: tiles, backlog: [] } });
      return;
    }

    if (drawerStep && drawerStep in initialValuesByStep) {
      setInitialValues(initialValuesByStep[drawerStep as keyof typeof initialValuesByStep]);
    } else {
      setInitialValues(null);
    }
  }, [drawerStep, mode, draftTiles, editingTarget, setInitialValues, dashboardConfig]);

  const value = useMemo(
    () => ({
      drawerStep,
      setDrawerStep,
      closeDrawer,
      setHasChanges,
      isEditMode,
      setIsEditMode,
      editingTarget,
      setEditingTarget,
      preventClose,
      setPreventClose,
      confirmOpen,
      setConfirmOpen,
      handleAttemptClose,
      handleConfirmClose,
      handleCancelClose,
      handleAttemptBack
    }),
    [
      drawerStep,
      closeDrawer,
      hasChanges,
      setHasChanges,
      isEditMode,
      editingTarget,
      preventClose,
      confirmOpen,
      handleAttemptClose,
      handleConfirmClose,
      handleCancelClose,
      handleAttemptBack
    ]
  );

  return <DrawerStepsContext value={value}>{children}</DrawerStepsContext>;
};
